/**
 * Stripe Configuration
 *
 * Centralizes all Stripe-related configuration including price IDs,
 * tier mappings, and URLs.
 */

export type SubscriptionTier = 'free' | 'pro' | 'pro_plus' | 'dev';

/**
 * Stripe API version used across the application
 * Update this when upgrading Stripe API versions
 */
export const STRIPE_API_VERSION = '2024-12-18.acacia' as const;

/**
 * Legacy Stripe price IDs for backward compatibility
 * These are kept for existing subscriptions created before the current price IDs
 *
 * These can be overridden via environment variables:
 * - STRIPE_LEGACY_PRICE_ID_PRO
 * - STRIPE_LEGACY_PRICE_ID_PRO_PLUS
 *
 * Hardcoded defaults are maintained for backward compatibility and to ensure
 * existing subscriptions continue to work even if env vars are not set.
 */
const LEGACY_PRICE_ID_PRO = process.env.STRIPE_LEGACY_PRICE_ID_PRO || 'price_1TH4pcHxy638AxvcN1Iy47R0';
const LEGACY_PRICE_ID_PRO_PLUS = process.env.STRIPE_LEGACY_PRICE_ID_PRO_PLUS || 'price_1TH57rHxy638AxvcT2YIIyb5';

/**
 * Stripe Price IDs for each subscription tier
 * Update these with your actual Stripe price IDs from the dashboard
 */
export const STRIPE_PRICE_IDS = {
  pro: process.env.STRIPE_PRICE_ID_PRO || '',
  pro_plus: process.env.STRIPE_PRICE_ID_PRO_PLUS || '',
} as const;

/**
 * Maps Stripe price IDs to subscription tiers
 * Built dynamically from environment variables at module load time
 * This is used by the webhook handler to determine which tier to assign
 */
export const PRICE_TO_TIER_MAP: Record<string, 'pro' | 'pro_plus'> = (() => {
  const map: Record<string, 'pro' | 'pro_plus'> = {};

  const proPriceId = process.env.STRIPE_PRICE_ID_PRO?.trim();
  const proPlusPriceId = process.env.STRIPE_PRICE_ID_PRO_PLUS?.trim();

  if (proPriceId) {
    map[proPriceId] = 'pro';
  } else {
    console.warn('[Stripe Config] STRIPE_PRICE_ID_PRO not set');
  }

  if (proPlusPriceId) {
    map[proPlusPriceId] = 'pro_plus';
  } else {
    console.warn('[Stripe Config] STRIPE_PRICE_ID_PRO_PLUS not set');
  }

  // Backward compatibility: add legacy IDs if different from env vars
  if (proPriceId !== LEGACY_PRICE_ID_PRO && !map[LEGACY_PRICE_ID_PRO]) {
    console.warn(`[Stripe Config] Adding legacy Pro price ID: ${LEGACY_PRICE_ID_PRO}`);
    map[LEGACY_PRICE_ID_PRO] = 'pro';
  }

  if (proPlusPriceId !== LEGACY_PRICE_ID_PRO_PLUS && !map[LEGACY_PRICE_ID_PRO_PLUS]) {
    console.warn(`[Stripe Config] Adding legacy Pro+ price ID: ${LEGACY_PRICE_ID_PRO_PLUS}`);
    map[LEGACY_PRICE_ID_PRO_PLUS] = 'pro_plus';
  }

  return map;
})();

/**
 * Payment link URLs
 * These are the direct Stripe payment links for each plan
 */
export const STRIPE_PAYMENT_LINKS = {
  pro: {
    monthly: 'https://buy.stripe.com/5kQ00j9pbd6fdTn3cF9IQ0b',
    yearly: 'https://buy.stripe.com/5kQ00j9pbd6fdTn3cF9IQ0b', // Update if different
  },
  pro_plus: {
    monthly: 'https://buy.stripe.com/8x214nbxj5DNg1v00t9IQ0c',
    yearly: 'https://buy.stripe.com/8x214nbxj5DNg1v00t9IQ0c', // Update if different
  },
} as const;

/**
 * URLs for payment flow
 */
export const PAYMENT_URLS = {
  success: '/payment/success',
  cancel: '/',
} as const;

/**
 * Subscription tier display names
 */
export const TIER_DISPLAY_NAMES: Record<SubscriptionTier, string> = {
  free: 'Free',
  pro: 'Pro',
  pro_plus: 'Pro+',
  dev: 'Dev',
} as const;

/**
 * Monthly credit limits per tier
 */
export const TIER_CREDIT_LIMITS: Record<SubscriptionTier, number> = {
  free: 30,
  pro: 100,
  pro_plus: 500,
  dev: 999,
} as const;

/**
 * Helper function to validate if a Stripe price ID is configured
 */
export function isValidPriceId(priceId: string): boolean {
  return Object.values(PRICE_TO_TIER_MAP).includes(
    PRICE_TO_TIER_MAP[priceId] as 'pro' | 'pro_plus'
  );
}

/**
 * Get tier from price ID
 */
export function getTierFromPriceId(priceId: string): 'pro' | 'pro_plus' | null {
  return PRICE_TO_TIER_MAP[priceId] || null;
}
